import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Sparkles, Check, RefreshCw, HardHat, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKGROUNDS = [
  { id: 'neutral-gray', label: 'Neutral gray (badge standard)' },
  { id: 'company-blue', label: 'Corporate navy blue' },
  { id: 'warm-cream', label: 'Warm cream' },
  { id: 'safety-white', label: 'ISO safety white' }
];
const UNIFORMS = [
  { id: 'preserve', label: "Keep what I'm wearing" },
  { id: 'hi-vis', label: 'Hi-vis vest (orange)' },
  { id: 'polo', label: 'Company polo' },
  { id: 'coveralls', label: 'Coveralls' }
];

export default function CapturePage() {
  const { token } = useParams();
  const fileInput = useRef(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [loadingLink, setLoadingLink] = useState(true);
  const [linkError, setLinkError] = useState('');

  const [preview, setPreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [background, setBackground] = useState('neutral-gray');
  const [uniform, setUniform] = useState('preserve');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/magic-links/${token}`)
      .then((res) => {
        setLinkInfo(res.data);
        setName(res.data.worker_name || '');
        setRole(res.data.worker_role || '');
      })
      .catch(() => setLinkError('This invite link is invalid or has expired. Ask your admin for a new one.'))
      .finally(() => setLoadingLink(false));
  }, [token]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSelfieFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
    setResult(null);
  };

  const generate = async () => {
    if (!selfieFile) return;
    setGenerating(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('selfie', selfieFile);
      form.append('background', background);
      form.append('uniform', uniform);
      form.append('worker_name', name || 'New crew member');
      form.append('worker_role', role || 'Field worker');
      form.append('site', linkInfo.site || '');
      form.append('magic_link_token', token);
      const res = await axios.post(`${API}/headshots/generate`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      setResult({ id: res.data.id, img: `${process.env.REACT_APP_BACKEND_URL}${res.data.image_url}` });
    } catch (err) {
      setLinkError(err.response?.data?.detail || 'Could not generate your headshot. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const submit = async () => {
    if (!result) return;
    try {
      await axios.post(`${API}/magic-links/${token}/submit/${result.id}`);
      setSubmitted(true);
    } catch {
      setLinkError('Could not submit your headshot. Please try again.');
    }
  };

  if (loadingLink) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#ea9a6f] animate-spin" /></div>;
  }

  if (linkError && !linkInfo) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center px-5">
        <div className="text-center max-w-sm" data-testid="capture-link-error">
          <AlertTriangle className="w-8 h-8 text-[#ea9a6f] mx-auto mb-3" />
          <p className="text-white">{linkError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center px-5">
        <div className="text-center max-w-sm" data-testid="capture-success-screen">
          <div className="w-14 h-14 rounded-full bg-[#7dd4c5]/15 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-[#7dd4c5]" />
          </div>
          <h2 className="serif text-2xl text-white mb-2">Submitted for review</h2>
          <p className="text-[#8a877f] text-sm">Thanks {name || 'there'}! Your headshot is with your admin for approval. You're all set.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <div className="max-w-md mx-auto px-5 pt-10 pb-16">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-[#ea9a6f] flex items-center justify-center">
            <HardHat className="w-4 h-4 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="serif text-2xl text-white">Crew<span className="text-[#ea9a6f]">Face</span></span>
        </div>
        <h1 className="serif text-3xl text-white mb-1">Snap your headshot</h1>
        <p className="text-[#8a877f] text-sm mb-6">No account needed. Takes about a minute.</p>

        <div
          onClick={() => fileInput.current?.click()}
          data-testid="capture-upload-zone"
          className="aspect-[3/4] rounded-xl border-2 border-dashed border-[#2a2a30] bg-[#101014] flex flex-col items-center justify-center cursor-pointer hover:border-[#ea9a6f] transition-colors overflow-hidden relative mb-4"
        >
          {result ? (
            <img src={result.img} alt="result" className="w-full h-full object-cover" data-testid="capture-result-image" />
          ) : preview ? (
            <img src={preview} alt="selfie preview" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-5">
              <Camera className="w-8 h-8 text-[#5a5850] mx-auto mb-3" />
              <div className="text-white text-sm font-medium">Tap to take or upload a selfie</div>
            </div>
          )}
          <input ref={fileInput} onChange={onFile} type="file" accept="image/*" capture="user" className="hidden" data-testid="capture-file-input" />
          {generating && (
            <div className="absolute inset-0 bg-[#0a0a0c]/80 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-[#ea9a6f] animate-spin" />
            </div>
          )}
        </div>

        {!result && (
          <div className="space-y-3 mb-4">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="capture-name-input" className="w-full bg-[#101014] border border-[#26262e] rounded-md h-11 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Your role" data-testid="capture-role-input" className="w-full bg-[#101014] border border-[#26262e] rounded-md h-11 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
            <Select value={background} onValueChange={setBackground}>
              <SelectTrigger className="bg-[#101014] border-[#26262e] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#101014] border-[#26262e] text-white">
                {BACKGROUNDS.map((b) => <SelectItem key={b.id} value={b.id} className="focus:bg-[#1a1a20] focus:text-white">{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={uniform} onValueChange={setUniform}>
              <SelectTrigger className="bg-[#101014] border-[#26262e] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#101014] border-[#26262e] text-white">
                {UNIFORMS.map((u) => <SelectItem key={u.id} value={u.id} className="focus:bg-[#1a1a20] focus:text-white">{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {linkError && <p className="text-xs text-red-400 mb-3" data-testid="capture-error-message">{linkError}</p>}

        {!result ? (
          <Button onClick={generate} disabled={!selfieFile || generating} data-testid="capture-generate-button" className="w-full bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-12">
            {generating ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate my headshot</>)}
          </Button>
        ) : (
          <div className="space-y-2">
            <Button onClick={submit} data-testid="capture-submit-button" className="w-full bg-[#7dd4c5] hover:bg-[#6bc4b5] text-[#0a0a0c] font-medium rounded-full h-12">
              <Check className="w-4 h-4 mr-2" /> Submit for review
            </Button>
            <Button onClick={generate} variant="outline" data-testid="capture-regenerate-button" className="w-full border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-11">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
